'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'

const STORAGE_KEY = 'site-animations-enabled'
const LEGACY_STORAGE_KEY = 'stamm-baum-animations-enabled'

interface AnimationContextType {
    animationsEnabled: boolean
    toggleAnimations: () => void
}

const AnimationContext = createContext<AnimationContextType>({
    animationsEnabled: true,
    toggleAnimations: () => { },
})

export const AnimationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [animationsEnabled, setAnimationsEnabled] = useState(true)

    // Read from localStorage on mount (migrating from legacy key if present)
    useEffect(() => {
        try {
            let stored = localStorage.getItem(STORAGE_KEY)
            if (stored === null) {
                stored = localStorage.getItem(LEGACY_STORAGE_KEY)
                if (stored !== null) {
                    try {
                        localStorage.setItem(STORAGE_KEY, stored)
                    } catch {
                        // ignore
                    }
                }
            }
            if (stored !== null) {
                setAnimationsEnabled(stored === 'true')
            } else {
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
                const isMobile = window.innerWidth < 768
                
                if (prefersReducedMotion || isMobile) {
                    setAnimationsEnabled(false)
                }
            }
        } catch {
            // localStorage not available (SSR or private mode)
        }
    }, [])

    const toggleAnimations = () => {
        setAnimationsEnabled((prev) => {
            const next = !prev
            try {
                localStorage.setItem(STORAGE_KEY, String(next))
            } catch {
                // ignore
            }
            return next
        })
    }

    return (
        <AnimationContext.Provider value={{ animationsEnabled, toggleAnimations }}>
            {children}
        </AnimationContext.Provider>
    )
}

export const useAnimation = () => useContext(AnimationContext)
